import React, { useState } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useCookieConsent } from '../contexts/CookieConsentContext';
import axios from 'axios';

interface UserInfo {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

interface PrivacyDataSectionProps {
  user: UserInfo;
}

const PrivacyDataSection: React.FC<PrivacyDataSectionProps> = ({ user }) => {
  const { t } = useTranslation();
  const { consent, getConsentSummary, resetConsent } = useCookieConsent();
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  /**
   * Export user data as JSON
   */
  const handleExportData = async () => {
    setIsExporting(true);
    setError('');
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/user/export-data', {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Create downloadable JSON file
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = window.URL.createObjectURL(dataBlob);
      
      // Download file
      const link = document.createElement('a');
      link.href = url;
      link.download = `vst-shop-data-${user.id}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setMessage(t('gdpr.export_success'));
    } catch (error: any) {
      setError(t('gdpr.export_error'));
      console.error('Export error:', error);
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Delete user account permanently
   */
  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      setError(t('gdpr.delete_confirmation_wrong'));
      return;
    }

    setIsDeleting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      await axios.delete('/api/user/delete-account', {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Clear all local data
      localStorage.clear();
      sessionStorage.clear();
      
      // Redirect to home
      window.location.href = '/';
    } catch (error: any) {
      setError(t('gdpr.delete_error'));
      console.error('Delete error:', error);
      setIsDeleting(false);
    }
  };

  /**
   * Reset cookie consent
   */
  const handleResetCookieConsent = () => {
    resetConsent();
    setMessage(t('gdpr.consent_reset_success'));
  };

  return (
    <div className="border-t border-gray-700 pt-6 mt-6">
      <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 2.676-.732 5.016-2.297 6.624-4.622C19.098 14.528 19.5 13.301 19.5 12c0-1.042-.133-2.052-.382-3.016z" />
        </svg>
        {t('gdpr.privacy_data_title')}
      </h3>

      {error && (
        <div className="bg-red-500/20 border border-red-500/50 text-red-300 px-4 py-3 rounded-md mb-4">
          {error}
        </div>
      )}

      {message && (
        <div className="bg-green-500/20 border border-green-500/50 text-green-300 px-4 py-3 rounded-md mb-4">
          {message}
        </div>
      )}

      <div className="space-y-6">
        {/* Cookie Consent Status */}
        <div className="bg-gray-700/30 border border-gray-600 rounded-lg p-4">
          <h4 className="text-white font-medium mb-2">{t('gdpr.cookie_preferences')}</h4>
          <p className="text-gray-400 text-sm mb-3">{getConsentSummary()}</p>
          {consent && (
            <div className="grid grid-cols-2 gap-2 text-xs mb-3">
              <div className="flex justify-between">
                <span className="text-gray-400">Essential:</span>
                <span className="text-green-400">✓ Active</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Analytics:</span>
                <span className={consent.analytics ? "text-green-400" : "text-red-400"}>
                  {consent.analytics ? "✓ Active" : "✗ Disabled"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Marketing:</span>
                <span className={consent.marketing ? "text-green-400" : "text-red-400"}>
                  {consent.marketing ? "✓ Active" : "✗ Disabled"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Functional:</span>
                <span className={consent.functional ? "text-green-400" : "text-red-400"}>
                  {consent.functional ? "✓ Active" : "✗ Disabled"}
                </span>
              </div>
            </div>
          )}
          <button
            onClick={handleResetCookieConsent}
            className="text-sm bg-gray-600 hover:bg-gray-500 text-white px-3 py-1 rounded transition-colors"
          >
            {t('gdpr.change_cookie_preferences')}
          </button>
        </div>

        {/* Data Export */}
        <div className="bg-gray-700/30 border border-gray-600 rounded-lg p-4">
          <h4 className="text-white font-medium mb-2">{t('gdpr.export_data_title')}</h4>
          <p className="text-gray-400 text-sm mb-3">
            {t('gdpr.export_data_description')}
          </p>
          <button
            onClick={handleExportData}
            disabled={isExporting}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white px-4 py-2 rounded transition-colors text-sm font-medium"
          >
            {isExporting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('gdpr.exporting')}
              </>
            ) : (
              t('gdpr.export_data_button')
            )}
          </button>
        </div>

        {/* Account Deletion */}
        <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4">
          <h4 className="text-red-300 font-medium mb-2">{t('gdpr.delete_account_title')}</h4>
          <p className="text-gray-400 text-sm mb-3">
            {t('gdpr.delete_account_warning')}
          </p>

          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded transition-colors text-sm font-medium"
            >
              {t('gdpr.delete_account_button')}
            </button>
          ) : (
            <div className="space-y-3">
              <p className="text-red-300 text-sm font-medium">
                {t('gdpr.delete_confirmation_prompt')}
              </p>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={t('gdpr.delete_confirmation_placeholder')}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleDeleteAccount}
                  disabled={isDeleting || deleteConfirmText !== 'DELETE'}
                  className="bg-red-600 hover:bg-red-700 disabled:bg-gray-600 text-white px-4 py-2 rounded transition-colors text-sm font-medium"
                >
                  {isDeleting ? t('gdpr.deleting') : t('gdpr.confirm_delete')}
                </button>
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmText('');
                    setError('');
                  }}
                  className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded transition-colors text-sm"
                >
                  {t('gdpr.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrivacyDataSection;